import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";

export default function CocoonMini() {
  const { activeId, apiFetch, dark, t, card, setView, proj } = useApp();
  const containerRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/site-scan/link-graph`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.nodes && d.nodes.length) setData(d); else setError(true); })
      .catch(() => setError(true));
  }, [activeId, apiFetch]);

  useEffect(() => {
    if (!data || !containerRef.current) return;
    const container = containerRef.current;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "width:100%;height:100%;border:none;display:block;border-radius:12px;";
    container.innerHTML = "";
    container.appendChild(iframe);

    const nodesJSON = JSON.stringify(data.nodes);
    const edgesJSON = JSON.stringify(data.edges);
    const extNodesJSON = JSON.stringify(data.externalNodes || []);

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{overflow:hidden;background:#050508;cursor:grab}
body:active{cursor:grabbing}
canvas{display:block;width:100%;height:100%}
</style></head><body>
<canvas id="c"></canvas>
<script type="importmap">
{"imports":{"three":"https://cdn.jsdelivr.net/npm/three@0.162.0/build/three.module.js","three/addons/":"https://cdn.jsdelivr.net/npm/three@0.162.0/examples/jsm/"}}
</script>
<script type="module">
import*as THREE from'three';
import{OrbitControls}from'three/addons/controls/OrbitControls.js';
import{EffectComposer}from'three/addons/postprocessing/EffectComposer.js';
import{RenderPass}from'three/addons/postprocessing/RenderPass.js';
import{UnrealBloomPass}from'three/addons/postprocessing/UnrealBloomPass.js';
import{OutputPass}from'three/addons/postprocessing/OutputPass.js';

const nodes=${nodesJSON};
const edges=${edgesJSON};
const extNodes=${extNodesJSON};

const urlIdx=new Map();
nodes.forEach((n,i)=>urlIdx.set(n.url,i));
const allEdges=[];const edgeSet=new Set();
edges.forEach(e=>{const fi=urlIdx.get(e.from),ti=urlIdx.get(e.to);if(fi!==undefined&&ti!==undefined){const k=Math.min(fi,ti)+','+Math.max(fi,ti);if(!edgeSet.has(k)){edgeSet.add(k);allEdges.push([fi,ti])}}});
let edgePairs=allEdges;
if(allEdges.length>3000){const tc=new Array(nodes.length).fill(0);allEdges.forEach(([a,b])=>{tc[a]++;tc[b]++});allEdges.sort((a,b)=>(tc[b[0]]+tc[b[1]])-(tc[a[0]]+tc[a[1]]));edgePairs=allEdges.slice(0,3000)}

const connCount=new Array(nodes.length).fill(0);
edgePairs.forEach(([a,b])=>{connCount[a]++;connCount[b]++});
let maxConn=1;for(let i=0;i<connCount.length;i++){if(connCount[i]>maxConn)maxConn=connCount[i]}

const getDepth=(url)=>{try{const p=new URL(url).pathname.replace(/\\/+$/,'');return p?p.split('/').filter(Boolean).length:0}catch{return 0}};
const ga=Math.PI*(3-Math.sqrt(5));
const N=nodes.length;

const depthGroups=new Map();
nodes.forEach((n,i)=>{const d=getDepth(n.url);if(!depthGroups.has(d))depthGroups.set(d,[]);depthGroups.get(d).push(i)});
const depths=[...depthGroups.keys()].sort((a,b)=>a-b);
const ringR=[0,6,13,20,27,34];
const positions=new Array(N);

depths.forEach(d=>{
  const group=depthGroups.get(d);
  group.sort((a,b)=>connCount[b]-connCount[a]);
  const baseR=ringR[Math.min(d,ringR.length-1)]||(34+d*6);
  if(d===0){group.forEach((idx)=>{positions[idx]=new THREE.Vector3((Math.random()-.5)*.5,(Math.random()-.5)*.5,(Math.random()-.5)*.5)});}
  else{group.forEach((idx,rank)=>{
    const orphanPush=connCount[idx]<3?(3-connCount[idx])*2.5:0;
    const r=baseR+orphanPush+(rank/Math.max(group.length-1,1))*2;
    const y=1-(rank/Math.max(group.length-1,1))*2;
    const rad=Math.sqrt(1-y*y)*r;
    const theta=ga*rank;
    positions[idx]=new THREE.Vector3(rad*Math.cos(theta)+(Math.random()-.5)*.4,y*r+(Math.random()-.5)*.4,rad*Math.sin(theta)+(Math.random()-.5)*.4);
  });}
});

function connColor(count,max){
  const t=Math.pow(count/Math.max(max,1),0.4);
  if(t<0.15)return new THREE.Color(0.9,0.15,0.15);
  if(t<0.35)return new THREE.Color(0.95,0.6,0.1);
  if(t<0.55)return new THREE.Color(0.3,0.85,0.5);
  if(t<0.75)return new THREE.Color(0.2,0.7,0.9);
  return new THREE.Color(0.5,0.3,0.9);
}

// Scene
const canvas=document.getElementById('c');
const scene=new THREE.Scene();
scene.fog=new THREE.FogExp2(0x000000,0.003);
const camera=new THREE.PerspectiveCamera(65,window.innerWidth/window.innerHeight,0.1,500);
camera.position.set(0,15,40);
const renderer=new THREE.WebGLRenderer({canvas,antialias:true,alpha:false});
renderer.setSize(window.innerWidth,window.innerHeight);
renderer.setPixelRatio(window.devicePixelRatio||2);
renderer.toneMapping=THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure=1.2;

const controls=new OrbitControls(camera,renderer.domElement);
controls.enableDamping=true;controls.dampingFactor=0.05;
controls.autoRotate=true;controls.autoRotateSpeed=0.8;
controls.enableZoom=false;controls.enablePan=false;

// Bloom
const composer=new EffectComposer(renderer);
composer.addPass(new RenderPass(scene,camera));
const bloom=new UnrealBloomPass(new THREE.Vector2(window.innerWidth,window.innerHeight),1.2,0.6,0.2);
composer.addPass(bloom);
composer.addPass(new OutputPass());

// Node points
const geo=new THREE.BufferGeometry();
const pos=new Float32Array(N*3);
const colors=new Float32Array(N*3);
const sizes=new Float32Array(N);
for(let i=0;i<N;i++){
  pos[i*3]=positions[i].x;pos[i*3+1]=positions[i].y;pos[i*3+2]=positions[i].z;
  const c=connColor(connCount[i],maxConn);
  colors[i*3]=c.r;colors[i*3+1]=c.g;colors[i*3+2]=c.b;
  const d=getDepth(nodes[i].url);
  const totalLinks=nodes[i].linksIn+nodes[i].linksOut;
  sizes[i]=d===0?2.5:totalLinks===0?0.5:Math.max(0.4,Math.min(1.8,0.4+totalLinks*0.04));
}
geo.setAttribute('position',new THREE.BufferAttribute(pos,3));
geo.setAttribute('color',new THREE.BufferAttribute(colors,3));
geo.setAttribute('size',new THREE.BufferAttribute(sizes,1));

const ptMat=new THREE.ShaderMaterial({
  vertexShader:\`attribute float size;varying vec3 vColor;void main(){vColor=color;vec4 mv=modelViewMatrix*vec4(position,1.0);gl_PointSize=size*(200.0/-mv.z);gl_Position=projectionMatrix*mv;}\`,
  fragmentShader:\`varying vec3 vColor;void main(){float d=length(gl_PointCoord-0.5);if(d>0.5)discard;float a=smoothstep(0.5,0.2,d);gl_FragColor=vec4(vColor*1.4,a*0.9);}\`,
  transparent:true,vertexColors:true,depthWrite:false,blending:THREE.AdditiveBlending
});
scene.add(new THREE.Points(geo,ptMat));

// Edges
const linePos=[];
edgePairs.forEach(([a,b])=>{linePos.push(positions[a].x,positions[a].y,positions[a].z,positions[b].x,positions[b].y,positions[b].z)});
const lineGeo=new THREE.BufferGeometry();
lineGeo.setAttribute('position',new THREE.Float32BufferAttribute(linePos,3));
scene.add(new THREE.LineSegments(lineGeo,new THREE.LineBasicMaterial({color:0x1a1040,transparent:true,opacity:0.15})));

// External donor nodes
extNodes.slice(0,30).forEach(en=>{
  const angle=Math.random()*Math.PI*2;
  const phi=Math.random()*Math.PI;
  const r=42+Math.random()*8;
  const x=r*Math.sin(phi)*Math.cos(angle),y=r*Math.cos(phi),z=r*Math.sin(phi)*Math.sin(angle);
  const sg=new THREE.SphereGeometry(0.4,6,6);
  const sm=new THREE.MeshBasicMaterial({color:0xfbbf24,transparent:true,opacity:0.7});
  const m=new THREE.Mesh(sg,sm);m.position.set(x,y,z);scene.add(m);
});

// Animate
function animate(){requestAnimationFrame(animate);controls.update();composer.render()}
animate();

window.addEventListener('resize',()=>{
  camera.aspect=window.innerWidth/window.innerHeight;camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth,window.innerHeight);
  composer.setSize(window.innerWidth,window.innerHeight);
});
<\/script></body></html>`;

    const iDoc = iframe.contentDocument || iframe.contentWindow.document;
    iDoc.open();
    iDoc.write(html);
    iDoc.close();

    return () => { container.innerHTML = ""; };
  }, [data]);

  if (error || !data) return null;

  const totalNodes = data.nodes.length;
  const totalEdges = data.edges.length;

  return (
    <div
      style={{
        ...card, padding: 0, borderRadius: 14, overflow: "hidden", cursor: "pointer",
        border: `1px solid ${dark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)"}`,
        position: "relative",
      }}
    >
      {/* 3D Scene */}
      <div ref={containerRef} style={{ width: "100%", height: 220, background: "#050508", borderRadius: "14px 14px 0 0" }} />

      {/* Overlay stats */}
      <div style={{
        position: "absolute", top: 12, left: 14,
        background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)",
        padding: "6px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14 }}>🕸️</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>SEO Кокон</span>
        </div>
      </div>

      {/* Bottom stats bar */}
      <div
        onClick={() => setView("seo_cocoon")}
        style={{
          padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
          background: dark ? "#0f0f14" : "#fff",
          borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
        }}
      >
        <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#6366f1" }}>{totalNodes}</div>
            <div style={{ color: t.textMuted }}>сторінок</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#8b5cf6" }}>{totalEdges}</div>
            <div style={{ color: t.textMuted }}>зв'язків</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#a855f7" }}>{totalNodes > 0 ? (totalEdges / totalNodes).toFixed(1) : 0}</div>
            <div style={{ color: t.textMuted }}>сер./стор.</div>
          </div>
        </div>
        <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>Відкрити →</span>
      </div>
    </div>
  );
}
